/**
 * **Bot 策略（纯函数、pack 无关）。**
 *
 * 只依赖规则层的 `options(state, actor, ctx)`（它每条都真的喂过 `reduce`），
 * 以及牌的 `traits` / 动作里的目标 —— **不读牌名、不写死原版包**。随机数由调用方
 * 喂 `random`（种子化即可复现）。
 *
 * UI 不依赖这一层；`dev/agent.ts` 只是它的一个默认 registry + `Math.random` 包装。
 */

import type { GameAction, ReduceContext } from '../core/actions';
import type { PlayerId } from '../core/ids';
import { nopeEligible, type GameState } from '../core/state';
import { options as enumerateOptions, type Option } from '../core/options';

export type BotStyle =
  /** 均衡：喜欢出牌，也肯抽牌 */
  | 'balanced'
  /** 只打会影响别人的牌，把压力叠上去 */
  | 'aggressive'
  /** 从不出牌，只会抽牌 */
  | 'hoarder'
  /** 只打连击（一对 / 三张 / 五张不同名） */
  | 'comboHunter';

export interface BotProfile {
  readonly name: string;
  readonly style: BotStyle;
  /** 抽到致命牌用不用救命牌 */
  readonly rescue: 'always' | 'never' | 'random';
  /** 响应窗口里打出断点的概率（0–1） */
  readonly nopeChance: number;
  /** 待抉择里选“送死”的概率 */
  readonly declineChance: number;
}

export const BOT_PROFILES: readonly BotProfile[] = [
  { name: '随机', style: 'balanced', rescue: 'random', nopeChance: 0.25, declineChance: 0.5 },
  { name: '休想狂', style: 'balanced', rescue: 'always', nopeChance: 1, declineChance: 0 },
  { name: '攻击狂', style: 'aggressive', rescue: 'always', nopeChance: 0.3, declineChance: 0 },
  { name: '囤牌党', style: 'hoarder', rescue: 'always', nopeChance: 0.1, declineChance: 0 },
  { name: '送死鬼', style: 'balanced', rescue: 'never', nopeChance: 0.2, declineChance: 1 },
  { name: '连击猎手', style: 'comboHunter', rescue: 'always', nopeChance: 0.3, declineChance: 0 },
];

/** 该谁出面推动局面（和“轮到谁”不是一回事：窗口里可能只有房主能关） */
export function botActor(
  state: GameState,
  ctx: ReduceContext,
  random: () => number = Math.random,
): PlayerId | undefined {
  if (state.decision !== null) return state.decision.player;
  if (state.resolution !== null) {
    const first = nopeEligible(state, ctx.registry)[0];
    if (first !== undefined && random() < 0.5) return first;
    return state.host;
  }
  return state.turn?.player;
}

/** 牌类：这个选项会不会影响**别的玩家**（需要目标 / 连击指向别人）—— pack 无关 */
export function targetsOther(option: Option): boolean {
  const action = option.action;
  if (action.t !== 'turn/play') return false;
  return (action.play as { readonly target?: PlayerId | null }).target != null;
}

function pick<T>(items: readonly T[], random: () => number): T | undefined {
  if (items.length === 0) return undefined;
  return items[Math.floor(random() * items.length)];
}

/**
 * **挑一个动作。** 保证：返回的动作一定来自 `options()`，所以是合法的；
 * `undefined` 表示“现在没有可选动作”（调用方按卡死处理）。
 */
export function chooseBotAction(params: {
  readonly state: GameState;
  readonly actor: PlayerId;
  readonly ctx: ReduceContext;
  readonly profile: BotProfile;
  readonly random: () => number;
}): GameAction | undefined {
  const { state, actor, ctx, profile, random } = params;
  const all = enumerateOptions(state, actor, ctx);

  // ---- 待抉择 ----
  if (state.decision !== null && state.decision.player === actor) {
    const pending = state.decision;
    if (pending.kind === 'useRescue') {
      const use =
        profile.rescue === 'always'
          ? true
          : profile.rescue === 'never'
            ? false
            : random() < 1 - profile.declineChance;
      return { t: 'decide', actor, decision: { kind: 'useRescue', use } };
    }
    return pick(all, random)?.action;
  }

  // ---- 打断窗口：房主负责关窗（不做这一步整局会卡死） ----
  if (state.resolution !== null) {
    if (actor === state.host) return { t: 'window/timeout', actor: state.host };
    const nope = all.find((option) => option.tag === 'nope');
    if (nope !== undefined && random() < profile.nopeChance) return nope.action;
    return nope?.action;
  }

  // ---- 正常回合 ----
  const draws = all.filter((option) => option.tag === 'draw');
  const plays = all.filter((option) => option.tag.startsWith('play-'));

  switch (profile.style) {
    case 'hoarder':
      return draws[0]?.action ?? pick(plays, random)?.action;
    case 'aggressive': {
      const attacks = plays.filter((option) => targetsOther(option));
      if (attacks.length > 0 && random() < 0.9) return pick(attacks, random)?.action;
      break;
    }
    case 'comboHunter': {
      const combos = plays.filter(
        (option) =>
          option.tag === 'play-pair' ||
          option.tag === 'play-triple' ||
          option.tag === 'play-five',
      );
      if (combos.length > 0) return pick(combos, random)?.action;
      break;
    }
    case 'balanced':
      break;
  }

  if (plays.length > 0 && random() < 0.7) return pick(plays, random)?.action;
  return draws[0]?.action ?? pick(plays, random)?.action;
}
